import {Box, Stack} from "@mantine/core";
import UserMessage from "./UserMessage";
import AssistantMessage from "./AssistantMessage";
import {ExperimentalEngineIndicator} from "../components/ExperimentalEngineIndicator";
import {
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import classes from "../components/scrollContainer.module.css";
import threadClasses from "./Thread.module.css";
import {useAppDispatch, useAppSelector} from "../../../app/hooks";
import {bffApi} from "../bffApi";
import {ChatInput} from "./ChatInput";
import {useChatScroll} from "../hooks/useChatScroll";
import {useSidebarPosition, SidebarPosition} from "../hooks/useSidebarPosition";
import {MessageWithMetadata} from "../../../types";
import {useReactToPrint} from "react-to-print";
import {ChatViewContext} from "./ChatView";
import {useLocation} from "react-router-dom";
import {clearTransientStore} from "../transientChatSlice";
import {ContextSidebar} from "./context-sidebar/ContextSidebar";
import {CitationHighlightProvider} from "./CitationHighlightContext";
import {AppContext} from "../AppContext";
import {useNavbarWidth} from "../hooks/useNavbarWidth";
import {useMediaQuery} from "@mantine/hooks";

type ThreadProps = {
    id?: string;
    chatDisabled: boolean;
};

// Helper to convert rem to px (adjust multiplier if base font size differs)
const remToPx = (rem: number) => rem * 16;

export const Thread = ({id, chatDisabled}: ThreadProps) => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const userData = bffApi.endpoints.getUser.useQuery();
    const messages = useAppSelector((state) => state.chat.messages);
    const threadName = useAppSelector((state) => state.chat.name);
    const {isStreaming /* , filteredContent */} = useAppSelector(
        (state) => state.chat
    );
    const [sidebarPosition] = useSidebarPosition();
    const {navbarExpanded} = useContext(AppContext);
    const isDesktop = useMediaQuery("(min-width: 48rem)");

    // Calculate navbar width to account for when sidebar is on left
    const {expanded: navbarWidthExpanded, collapsed: navbarWidthCollapsed} =
        useNavbarWidth();
    const navbarWidth =
        isDesktop && navbarExpanded
            ? navbarWidthExpanded
            : navbarWidthCollapsed;

    const [animatingPosition, setAnimatingPosition] = useState<number | null>(
        null
    );
    const {
        scrollRef: viewport,
        isInitial,
        showScrollDownButton,
        forceScrollToBottom,
    } = useChatScroll([messages, animatingPosition]);
    const last = (messages?.length ?? 0) - 1;
    const lastUserMessageRef = useRef<HTMLDivElement>(null);
    const chatInputEstimatedHeight = 7;
    const containerRef = useRef<HTMLDivElement>(null);
    const [heightsMeasured, setHeightsMeasured] = useState(false);

    // Add print ref for the thread content
    const printRef = useRef<HTMLDivElement>(null);

    // Setup print handler
    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: threadName || "Thread",
        onPrintError: (error) => console.error("Print failed:", error),
    });

    // Set/update CSS variables based on the actual last user message
    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        // Set chat input height variable (convert rem to px)
        container.style.setProperty(
            "--chat-input-height-px",
            `${remToPx(chatInputEstimatedHeight)}px`
        );

        // Check if the relevant user message exists (index last - 1)
        const userMessageExists = last > 0; // Ensure there is a user message before the last assistant message
        const userMessageElement = lastUserMessageRef.current;

        if (userMessageExists && userMessageElement) {
            // Set user message height variable
            container.style.setProperty(
                "--last-user-message-height-px",
                `${userMessageElement.offsetHeight}px`
            );
        } else {
            container.style.setProperty("--last-user-message-height-px", `0px`);
        }
        setHeightsMeasured(true);
    }, [messages, chatInputEstimatedHeight]);

    useEffect(() => {
        viewport.current?.scrollTo(0, viewport.current?.scrollHeight);
    }, [viewport.current]);

    useEffect(() => {
        if (threadName) {
            document.title = threadName;
        }

        return () => {
            document.title = "KAI";
        };
    }, [threadName]);

    useEffect(() => {
        dispatch(clearTransientStore());
    }, [location]);

    const handleAnimationStart = useCallback((position: number) => {
        setAnimatingPosition(position);
    }, []);

    const handleAnimationFinish = useCallback((position: number) => {
        setAnimatingPosition((current) =>
            current === position ? null : current
        );
    }, []);

    const {sendMessage} = useContext(ChatViewContext);

    // Get first user message content for search query (works for new and existing chats)
    const firstUserMessage = messages?.find((m) => m.role === "user")?.content;

    // Wrapper function to handle message submission and force scroll
    const handleSendMessage = useCallback(
        (message: string) => {
            sendMessage(message); // Call original function
            // Ensure scroll happens after message is likely added (or state update starts)
            requestAnimationFrame(() => {
                forceScrollToBottom();
            });
        },
        [sendMessage, forceScrollToBottom]
    );

    return (
        <CitationHighlightProvider>
            <Box
                ref={containerRef}
                key={id}
                style={{
                    display: "flex",
                    flexDirection: "row",
                    height: "100%",
                    overflow: "hidden",
                }}
            >
                {/* Context Sidebar on left */}
                {sidebarPosition === SidebarPosition.Left && (
                    <ContextSidebar
                        title={threadName}
                        searchQuery={firstUserMessage}
                        position={sidebarPosition}
                        navbarWidth={isDesktop ? navbarWidth : 0}
                    />
                )}

                <Box
                    style={{
                        flex: "1 1 100%",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        overflow: "hidden",
                        position: "relative",
                        minWidth: 0,
                    }}
                >
                    <Box
                        ref={viewport}
                        className={`${classes.scrollbox} ${
                            !isInitial ? classes.smoothScroll : ""
                        }`}
                        style={{
                            flex: 1,
                            overflowY: "auto",
                            position: "relative",
                            paddingBottom: `${chatInputEstimatedHeight}rem`,
                            scrollPaddingBottom: `${chatInputEstimatedHeight}rem`,
                            marginBottom: `-${chatInputEstimatedHeight + 2}rem`,
                        }}
                        data-name="thread"
                    >
                        <Stack
                            ref={printRef}
                            className={threadClasses.threadbox}
                            justify="flex-start"
                            align="stretch"
                        >
                            <ExperimentalEngineIndicator />
                            {messages?.map(
                                (msg: MessageWithMetadata, i: number) =>
                                    msg.role === "user" ? (
                                        <UserMessage
                                            ref={
                                                i === last - 1
                                                    ? lastUserMessageRef
                                                    : null
                                            }
                                            key={`${id || "thread"}-user-${
                                                msg.position
                                            }`}
                                            message={msg}
                                            initials={
                                                userData.data?.initials ?? "U"
                                            }
                                        />
                                    ) : (
                                        <AssistantMessage
                                            key={`${id || "thread"}-assistant-${
                                                msg.position
                                            }`}
                                            message={msg}
                                            isStreaming={
                                                isStreaming && i === last
                                            }
                                            onAnimationStart={
                                                handleAnimationStart
                                            }
                                            onAnimationFinish={
                                                handleAnimationFinish
                                            }
                                            isLastMessageInThread={i === last}
                                            heightsMeasured={
                                                i === last
                                                    ? heightsMeasured
                                                    : undefined
                                            }
                                        />
                                    )
                            )}
                        </Stack>
                    </Box>
                    <ChatInput
                        disabled={chatDisabled}
                        onSubmit={handleSendMessage}
                        scrollRef={viewport}
                        showScrollDownButton={showScrollDownButton}
                    />
                </Box>

                {/* Context Sidebar on right */}
                {sidebarPosition === SidebarPosition.Right && (
                    <ContextSidebar
                        title={threadName}
                        searchQuery={firstUserMessage}
                        position={sidebarPosition}
                    />
                )}
            </Box>
        </CitationHighlightProvider>
    );
};
